import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  InputGroup,
  InputRightElement,
  VStack, 
  Heading, 
  useToast, 
  useColorModeValue,
  Image,
  Center,
  Checkbox,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { getPublicSettings, PublicSettings } from '../services/settings';
import { assetUrl } from '../utils/url';
import { PasswordHelpTooltip } from '../components/common/HelpTooltipCard';

interface LocationState {
  from: {
    pathname: string;
  };
}

const AuthPage: React.FC = () => {
  // All hooks must be called at the top level, before any conditional returns
  const { t } = useTranslation();
  const { isAuthenticated, adminExists, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const [settings, setSettings] = useState<PublicSettings | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');

  // Load public settings to display club logo
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const s = await getPublicSettings();
        if (mounted) setSettings(s);
      } catch (_) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);
  
  // If user is already authenticated, redirect to admin dashboard
  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  const from = (location.state as LocationState)?.from?.pathname || '/';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user } = response.data;
      login(token, user, remember);
      toast({
        title: 'Přihlášení proběhlo úspěšně',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      // Role-based redirect after login, honoring optional `from` target
      // and a stored post-setup target (fc_post_setup_target) for step 2
      const role = String(user?.role || '').toLowerCase();
      const targetFrom = (location.state as LocationState)?.from?.pathname;
      let storedTarget: string | null = null;
      try {
        storedTarget = localStorage.getItem('fc_post_setup_target');
        if (storedTarget) {
          localStorage.removeItem('fc_post_setup_target');
        }
      } catch {}
      if (role === 'admin' || role === 'editor') {
        const target = targetFrom || storedTarget || '/admin';
        navigate(target, { replace: true });
      } else if (role === 'user') {
        navigate('/', { replace: true });
      } else {
        // Fallback for unknown roles (e.g., fan): go to frontpage
        navigate('/', { replace: true });
      }
    } catch (error: any) {
      toast({
        title: 'Přihlášení selhalo',
        description: error.response?.data?.error || error.response?.data?.message || 'Neplatný e-mail nebo heslo',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Removed inline reset sender button; keep single link to forgot-password page/modal

  // Registration has been removed for initial setup flow; only login is shown

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
      <Box w="100%" maxW="md" p={8} bg={bgColor} borderRadius="lg" boxShadow="lg">
        <Center mb={4}>
          {settings?.club_logo_url ? (
            <Image src={assetUrl(settings.club_logo_url) || settings.club_logo_url} alt={settings.club_name || 'Club logo'} maxH="64px" objectFit="contain" />
          ) : (
            <Heading size="md">Přihlášení</Heading>
          )}
        </Center>
        <VStack as="form" onSubmit={handleLogin} spacing={4} align="stretch">
          <FormControl id="login-email" isRequired>
            <FormLabel>E-mailová adresa</FormLabel>
            <Input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Zadejte svůj e-mail"
            />
          </FormControl>
          <FormControl id="login-password">
            <FormLabel
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box as="span" display="inline-flex" alignItems="center">
                <Box as="span" mr={1}>Heslo</Box>
                <Box as="span" color="red.500">*</Box>
              </Box>
              <PasswordHelpTooltip />
            </FormLabel>
            <InputGroup>
              <Input 
                type={showPassword ? 'text' : 'password'} 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Zadejte své heslo"
                required
                aria-required="true"
              />
              <InputRightElement width="4.5rem">
                <Button h="1.75rem" size="sm" onClick={() => setShowPassword((s) => !s)}>{showPassword ? 'Skrýt' : 'Zobrazit'}</Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>
          <Checkbox isChecked={remember} onChange={(e) => setRemember(e.target.checked)}>Zapamatovat přihlášení</Checkbox>
          <Button 
            type="submit" 
            colorScheme="blue" 
            isLoading={isLoading} 
            loadingText="Přihlašuji..."
            mt={4}
          >
            {t('auth.login')}
          </Button>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <Button variant="link" colorScheme="blue" onClick={() => navigate('/forgot-password?admin=1')}>{t('auth.forgot_password')}</Button>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default AuthPage;
